document.addEventListener("DOMContentLoaded", function () {
  let translateSelectors = []; // To store selectors to be translated dynamically
  let skipSelectors = []; // To store selectors to be skipped
  const elementDataArray = []; // Use an array to track content and DOM elements (to avoid reprocessing)
  const mutationObserverConfig = {
    characterData: true,
    childList: true,
    subtree: true,
  };
  let translationTimeout = null; // Timeout to batch translation requests
  const processedElements = new WeakMap(); // Track elements -> original content mapping (allows re-translation if content changes)
  const shopLocale = window.Shopify?.locale
    ? getDeeplLocale(window.Shopify.locale)
    : "en"; // Default to English if locale is not available
  const CACHE_PREFIX = 'dt_'; // Prefix for localStorage keys

  // Function to generate cache key for a text and target language
  function getCacheKey(text, targetLang) {
    return `${CACHE_PREFIX}${targetLang}_${text}`;
  }

  // Function to get translation from cache
  function getFromCache(text, targetLang) {
    try {
      const key = getCacheKey(text, targetLang);
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage read error:', e);
      return null;
    }
  }

  // Function to save translation to cache
  function saveToCache(text, targetLang, translation) {
    try {
      const key = getCacheKey(text, targetLang);
      localStorage.setItem(key, translation);
    } catch (e) {
      console.warn('localStorage write error:', e);
    }
  }

  // Global function to clear translation cache
  window.clearTranslationCache = function() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      console.log('Translation cache cleared successfully');
      return true;
    } catch (e) {
      console.error('Error clearing translation cache:', e);
      return false;
    }
  };

  // Global function to dump all cached translations for debugging
  window.dumpTranslationCache = function() {
    try {
      const keys = Object.keys(localStorage);
      const translations = {};
      let count = 0;

      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          const value = localStorage.getItem(key);
          const cleanKey = key.substring(CACHE_PREFIX.length); // Remove prefix
          translations[cleanKey] = value;
          count++;
        }
      });

      console.log(`=== Translation Cache (${count} entries) ===`);
      console.table(translations);
      console.log('Full data:', translations);
      return translations;
    } catch (e) {
      console.error('Error dumping translation cache:', e);
      return null;
    }
  };

  // Debug helper to check translation state
  window.debugTranslationState = function(element) {
    if (!element) {
      console.log('=== Translation State ===');
      console.log('Element Data Array:', elementDataArray.length, elementDataArray);
      console.log('Translation Timeout:', translationTimeout !== null);
      return {
        queuedCount: elementDataArray.length,
        queuedItems: elementDataArray
      };
    } else {
      console.log('=== Element Translation State ===');
      console.log('Element:', element);
      console.log('Processed Content:', processedElements.get(element));
      console.log('Current Content:', element.textContent?.trim() || element.value || element.getAttribute('placeholder'));
      return {
        processedContent: processedElements.get(element),
        currentContent: element.textContent?.trim() || element.value || element.getAttribute('placeholder')
      };
    }
  };

  // Function to convert zh-CN and zh-TW to zh-HANS and zh-HANT
  function getDeeplLocale(locale) {
    if (locale === "zh-CN") return "ZH-HANS"; // Simplified Chinese
    if (locale === "zh-TW") return "ZH-HANT"; // Traditional Chinese
    return locale.toUpperCase(); // Return other locales unchanged
  }

  // ** Check if the shop locale is English **
  if (shopLocale.startsWith("EN")) {
    return; // Skip translation if the locale is English
  }

  // Function to check if text contains words (ignores numbers, currency, punctuation, etc.)
  function containsWords(text) {
    const wordRegex = /\b(?!\d+|[\p{Sc}\p{P}]+)([a-zA-Z]+)\b/u;
    return wordRegex.test(text); // Returns true if the text contains any words
  }

  // Function to check if text is already a translation (exists as a cached value)
  // This works for all languages, not just CJK
  function isAlreadyTranslated(text, targetLang) {
    try {
      const prefix = `${CACHE_PREFIX}${targetLang}_`;
      const keys = Object.keys(localStorage);
      
      // Check if this text exists as a cached translation value
      for (let key of keys) {
        if (key.startsWith(prefix)) {
          const cachedTranslation = localStorage.getItem(key);
          if (cachedTranslation === text) {
            return true; // This text is a translation result
          }
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  // Function to extract text content and placeholder attributes from nodes
  function extractContentFromNodes(node) {
    if (node.nodeType === 1) {
      // Element node
      // Skip elements with the class 'notranslate'
      if (node.classList.contains("notranslate")) {
        return;
      }

      // For inputs and textareas, check placeholders
      if (
        (node.nodeName === "INPUT" || node.nodeName === "TEXTAREA") &&
        node.getAttribute("placeholder")
      ) {
        const content = node.getAttribute("placeholder");
        // Skip if already processed with the same content
        if (processedElements.has(node) && processedElements.get(node) === content) {
          return;
        }
        // Skip if content is already a translation (universal check for all languages)
        if (isAlreadyTranslated(content, shopLocale)) {
          return;
        }
        if (containsWords(content) && !elementDataArray.some((data) => data.element === node)) {
          elementDataArray.push({ element: node, content, type: 1 });
        }
      }

      // For submit buttons, check the value attribute
      if (node.nodeName === "INPUT" && node.type === "submit") {
        const content = node.value;
        // Skip if already processed with the same content
        if (processedElements.has(node) && processedElements.get(node) === content) {
          return;
        }
        // Skip if content is already a translation (universal check for all languages)
        if (isAlreadyTranslated(content, shopLocale)) {
          return;
        }
        if (containsWords(content) && !elementDataArray.some((data) => data.element === node)) {
          elementDataArray.push({ element: node, content, type: 8 });
        }
      }

      // Recursively process all child nodes
      node.childNodes.forEach((child) => {
        extractContentFromNodes(child); // Process child nodes recursively
      });
    } else if (node.nodeType === 3 && node.textContent.trim() !== "") {
      // Text node
      
      const content = node.textContent.trim();
      
      // Skip text nodes that have already been processed with the same content
      if (processedElements.has(node) && processedElements.get(node) === content) {
        return;
      }
      
      // Skip if content is already a translation (universal check for all languages)
      if (isAlreadyTranslated(content, shopLocale)) {
        return;
      }
      
      // Skip if this doesn't contain English words
      if (!containsWords(content)) {
        return;
      }
      
      if (!elementDataArray.some((data) => data.element === node)) {
        // Add loading class instead of hiding (optional, user can style .dt-loading)
        if (node.parentElement && !node.parentElement.classList.contains('notranslate')) {
          node.parentElement.classList.add('dt-loading');
        }
        elementDataArray.push({ element: node, content, type: 3 }); // Store text content in array
      }
    }
  }

  // Function to apply cached translations immediately
  function applyCachedTranslations() {
    if (elementDataArray.length === 0) return;

    const itemsWithCache = [];
    const itemsNeedingAPI = [];

    // Separate cached from uncached items
    elementDataArray.forEach((dataItem) => {
      const cached = getFromCache(dataItem.content, shopLocale);
      if (cached) {
        itemsWithCache.push({ dataItem, translation: cached });
      } else {
        itemsNeedingAPI.push(dataItem);
      }
    });

    // Apply cached translations immediately
    itemsWithCache.forEach(({ dataItem, translation }) => {
      const { element, type, content } = dataItem;
      
      if (type === 1) {
        element.setAttribute("placeholder", translation);
        // Store translated content to track current state
        processedElements.set(element, translation);
      } else if (type === 3) {
        element.textContent = translation;
        if (element.parentElement) {
          element.parentElement.classList.remove('dt-loading');
        }
        // Store translated content to track current state
        processedElements.set(element, translation);
      } else if (type === 8) {
        element.value = translation;
        // Store translated content to track current state
        processedElements.set(element, translation);
      }
    });

    // Remove cached items from array and keep only items needing API call
    elementDataArray.length = 0;
    elementDataArray.push(...itemsNeedingAPI);
  }

  // Function to process and send the collected data to the Netlify function for translation
  async function translateData() {
    if (elementDataArray.length === 0) return; // No new data to process

    const batches = [];
    for (let i = 0; i < elementDataArray.length; i += 50) {
      batches.push(elementDataArray.slice(i, i + 50)); // Split into batches of 50 due to DeepL API limit
    }

    // Process each batch
    for (const batch of batches) {
      const textsToTranslate = batch.map((item) => item.content);
      const payload = {
        text: textsToTranslate,
        target_lang: shopLocale,
      };

      try {
        const response = await fetch(
          "https://deepl-translate.netlify.app/.netlify/functions/translate",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        const result = await response.json();
        if (result.success) {
          const translatedText = result.translation;
          batch.forEach((dataItem, idx) => {
            const translatedContent = translatedText[idx];
            const { element, type, content } = dataItem;
            
            // Save to cache only if translation is valid
            if (translatedContent != null && translatedContent !== '') {
              saveToCache(content, shopLocale, translatedContent);
            }
            
            if (type === 1) {
              element.setAttribute("placeholder", translatedContent);
              // Store translated content to track current state
              processedElements.set(element, translatedContent);
            } else if (type === 3) {
              element.textContent = translatedContent;
              if (element.parentElement) {
                element.parentElement.classList.remove('dt-loading');
              }
              // Store translated content to track current state
              processedElements.set(element, translatedContent);
            } else if (type === 8) {
              element.value = translatedContent;
              // Store translated content to track current state
              processedElements.set(element, translatedContent);
            }
          });
        } else {
          console.error("Translation failed:", result.error);
        }
      } catch (error) {
        console.error("Error during translation:", error);
      }
    }

    // Remove processed elements from the array
    elementDataArray.length = 0;
  }

  // Function to schedule batched translation requests (debounced)
  function scheduleTranslation() {
    // Apply cached translations immediately
    applyCachedTranslations();
    
    // Only schedule API call if there are uncached items
    if (elementDataArray.length > 0) {
      clearTimeout(translationTimeout);
      translationTimeout = setTimeout(() => {
        translateData();
      }, 100); // Reduced debounce for faster initial load while still batching
    }
  }

  // Observer to handle dynamically added elements
  const dynamicElementObserver = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            
            // Mark dynamically added elements for skipping translation
            if (skipSelectors.length > 0) {
              node.querySelectorAll(skipSelectors.join(", ")).forEach((element) => {
                element.classList.add("notranslate");
              });
            }
            
            // Process elements for translation
            translateSelectors.forEach((selector) => {
              if (node.matches(selector)) {
                translateTextInNodeAndDescendants(node);
              }
            });
          }
        });
      }
    });
  });

  // Observer for changes to text content and dynamically added child nodes
  const observedElementObserver = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === "childList") {
        // When childList changes (innerHTML replacement), we need to clear tracking
        // for removed nodes and process all new nodes
        mutation.removedNodes.forEach((removedNode) => {
          // Clear processedElements for removed nodes and their descendants
          const clearProcessedElements = (node) => {
            if (processedElements.has(node)) {
              processedElements.delete(node);
            }
            if (node.childNodes) {
              node.childNodes.forEach(clearProcessedElements);
            }
          };
          clearProcessedElements(removedNode);
        });
        
        // Extract content from the mutation target (will process all new child nodes)
        extractContentFromNodes(mutation.target);
        scheduleTranslation();
      } else if (mutation.type === "characterData") {
        // For characterData mutations, check if this is our own translation update
        // by verifying the current content matches what we stored
        const currentContent = mutation.target.textContent?.trim();
        const storedContent = processedElements.get(mutation.target);
        
        // Only process if content changed to something we haven't stored
        if (!storedContent || currentContent !== storedContent) {
          extractContentFromNodes(mutation.target);
          scheduleTranslation();
        }
      }
    });
  });

  // Function to observe a node and its descendants for translation
  function translateTextInNodeAndDescendants(node) {
    extractContentFromNodes(node); // Extract content from the node and its descendants
    observedElementObserver.observe(node, mutationObserverConfig); // Observe for further changes
    scheduleTranslation(); // Schedule translation for the initial content
  }

  if (window.theme.settings.skipTranslation.trim()) {
    // Split the string by newlines and trim each selector for skipping
    skipSelectors = window.theme.settings.skipTranslation
      .split("\n")
      .map((selector) => selector.trim());

    // Immediately mark matching elements with 'notranslate'
    skipSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.classList.add("notranslate");
      });
    });
  }

  // Initialize selectors and mark elements
  if (window.theme.settings.autoTranslation.trim()) {
    // Split the string by newlines and trim each selector
    translateSelectors = window.theme.settings.autoTranslation
      .split("\n")
      .map((selector) => selector.trim());

    // Immediately translate all elements matching the selectors
    translateSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        translateTextInNodeAndDescendants(element);
      });
    });
  }

  // Start observing the document body for dynamically added elements
  dynamicElementObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
